import { useEffect, useState } from "react";
import type { PrescriptionItem } from "../../types/dental";

interface PrescriptionListPanelProps {
  items: PrescriptionItem[];
  selectedItemId: string | null;
  onSelectItem: (itemId: string) => void;
}

function formatProcedureLabel(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatTarget(item: PrescriptionItem): string {
  const { target } = item;

  if (target.targetType === "range" && target.rangeStart && target.rangeEnd) {
    return `${target.jaw.toUpperCase()} • ${target.rangeStart}-${target.rangeEnd}`;
  }

  return `${target.jaw.toUpperCase()} • ${target.teeth.join(", ")}`;
}

function formatTargetTeeth(item: PrescriptionItem): string {
  const { target } = item;

  if (target.targetType === "range" && target.rangeStart && target.rangeEnd) {
    return `${target.rangeStart} - ${target.rangeEnd}`;
  }

  return target.teeth.length > 0 ? target.teeth.join(", ") : "-";
}

export function PrescriptionListPanel({
  items,
  selectedItemId,
  onSelectItem,
}: PrescriptionListPanelProps) {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(
    selectedItemId
  );

  useEffect(() => {
    setExpandedItemId(selectedItemId);
  }, [selectedItemId]);

  function handleToggleExpand(itemId: string): void {
    setExpandedItemId((current) => (current === itemId ? null : itemId));
  }

  return (
    <section className="prescription-panel">
      <div className="prescription-panel__header">
        <h3>Prescription Items</h3>
        <span>{items.length} items</span>
      </div>

      <div className="prescription-list">
        {items.length === 0 && (
          <div className="prescription-empty">No prescription items available.</div>
        )}

        {items.map((item) => {
          const isSelected = item.itemId === selectedItemId;
          const isExpanded = item.itemId === expandedItemId;

          return (
            <div
              key={item.itemId}
              className={`prescription-row ${
                isSelected ? "prescription-row--selected" : ""
              }`}
            >
              <div
                className="prescription-row__header"
                onClick={() => onSelectItem(item.itemId)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectItem(item.itemId);
                  }
                }}
              >
                <button
                  type="button"
                  className="prescription-row__expand"
                  aria-label={isExpanded ? "Collapse prescription" : "Expand prescription"}
                  aria-expanded={isExpanded}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleExpand(item.itemId);
                  }}
                >
                  {isExpanded ? "▾" : "▸"}
                </button>

                <div className="prescription-row__summary">
                  <div className="prescription-row__title">{item.title}</div>
                  <div className="prescription-row__meta">
                    <span>{formatProcedureLabel(item.procedureType)}</span>
                    <span>{formatTarget(item)}</span>
                    <span>{item.status}</span>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="prescription-row__details">
                  <div className="prescription-detail-grid">
                    <div>
                      <label>Procedure</label>
                      <div>{formatProcedureLabel(item.procedureType)}</div>
                    </div>

                    <div>
                      <label>Status</label>
                      <div>{item.status}</div>
                    </div>

                    <div>
                      <label>Jaw</label>
                      <div>{item.target.jaw}</div>
                    </div>

                    <div>
                      <label>Target Type</label>
                      <div>{item.target.targetType}</div>
                    </div>

                    <div>
                      <label>Target Teeth</label>
                      <div>{formatTargetTeeth(item)}</div>
                    </div>

                    <div>
                      <label>Material Requested</label>
                      <div>{item.material || "-"}</div>
                    </div>

                    <div>
                      <label>Shade Requested</label>
                      <div>{item.shade || "-"}</div>
                    </div>

                    <div>
                      <label>Production Notes</label>
                      <div>{item.productionNotes || "-"}</div>
                    </div>
                  </div>

                  <div className="prescription-detail-notes">
                    <label>Doctor Notes</label>
                    <div>{item.doctorNotes || "-"}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}